import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { BarChart3, Bot, Check, ChevronDown, CreditCard, Inbox, LayoutGrid, LogOut, ListOrdered, Paintbrush, PanelLeftClose, PanelLeftOpen, Shield, TicketPercent, Store } from 'lucide-react';
import { getStorePlatformLabel, isPlatformManagedStore, useConsoleStore, useActiveStore } from '../store';
import { apiFetch } from '../lib/api';
import { ReachLogo } from './Logo';
import { cn } from '../lib/utils';

const PRODUCT_NAV = [
  { to: '/appearance', icon: Paintbrush, label: 'Appearance & Layout' },
  { to: '/chatbot', icon: Bot, label: 'Chatbot' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/subscription', icon: CreditCard, label: 'Subscription' },
];
const SIDEBAR_COLLAPSED_KEY = 'reach_console_sidebar_collapsed';

export function Layout() {
  const location = useLocation();
  const { user, stores, activeStoreId, setActiveStoreId, logout } = useConsoleStore();
  const activeStore = useActiveStore();
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const storeMenuRef = useRef<HTMLDivElement | null>(null);
  const isInternal = user?.role === 'support' || user?.role === 'staff' || user?.role === 'superadmin';
  const isStaffPlus = user?.role === 'staff' || user?.role === 'superadmin';
  const activeStorePlatformLabel = getStorePlatformLabel(activeStore);
  const activeStoreMode = isPlatformManagedStore(activeStore) ? 'Platform managed' : 'Console workspace';
  const adminNavItems = [
    { to: '/internal', icon: Shield, label: 'Internal' },
    { to: '/beta-requests', icon: Inbox, label: 'Beta Requests' },
    { to: '/action-log', icon: ListOrdered, label: 'Action Log' },
    ...(isStaffPlus ? [{ to: '/promo-codes', icon: TicketPercent, label: 'Promo Codes' }] : []),
  ];
  const platformDotClass = (platform?: string) => {
    const p = (platform || '').toLowerCase();
    if (p === 'shopify') return 'console-platform-dot-shopify';
    if (p === 'wordpress') return 'console-platform-dot-wordpress';
    return 'console-platform-dot-standard';
  };

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!storeMenuRef.current?.contains(event.target as Node)) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      // Ignore storage failures in restricted browser contexts.
    }
  }, [sidebarCollapsed]);

  const handleLogout = async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      <aside className={cn('console-sidebar flex w-[260px] flex-shrink-0 flex-col', sidebarCollapsed && 'console-sidebar-collapsed')}>
        <div className="console-sidebar-brand-row flex min-h-[88px] items-center gap-4 px-4 pb-3 pt-3">
          <div className="console-brand-mark">
            <ReachLogo size="large" showText={false} color="rgba(255,255,255,0.95)" detailColor="var(--reach-logo-detail)" />
          </div>
          <div className="console-brand-copy min-w-0">
            <div className="console-brand-wordmark text-[26px] font-extrabold leading-none tracking-[0.06em] text-white">
              REACH<span className="console-brand-dot">.</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setSidebarCollapsed((collapsed) => !collapsed);
              setStoreMenuOpen(false);
            }}
            className="console-sidebar-collapse"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
          </button>
        </div>

        {activeStore && (
          <div className="console-store-card mx-3 mb-4 mt-3" ref={storeMenuRef}>
            <button
              type="button"
              onClick={() => setStoreMenuOpen((open) => !open)}
              className="console-store-trigger"
              aria-haspopup="listbox"
              aria-expanded={storeMenuOpen}
              title={activeStore.externalDomain}
            >
              <span className="console-store-icon"><Store size={14} /></span>
              <span className="console-store-trigger-copy min-w-0">
                <span className="block truncate">{activeStore.externalDomain}</span>
                <span className="console-sidebar-muted mt-1 block text-[11px] font-medium">{activeStore.planTier} plan · {activeStorePlatformLabel}</span>
              </span>
              <ChevronDown className={cn('console-store-chevron h-3.5 w-3.5 flex-shrink-0 transition-transform', storeMenuOpen && 'rotate-180')} />
            </button>

            {storeMenuOpen && (
              <div className="console-store-menu" role="listbox" aria-label="Select active store">
                {stores.map((store) => {
                  const selected = store.id === activeStore.id;
                  return (
                    <button
                      key={store.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        setActiveStoreId(store.id);
                        setStoreMenuOpen(false);
                      }}
                      className={cn('console-store-option', selected && 'console-store-option-active')}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold">{store.externalDomain}</span>
                        <span className="mt-1 flex items-center gap-1.5 text-[11px] font-medium">
                          <span className={cn('console-platform-dot', platformDotClass(store.platform))} />
                          {getStorePlatformLabel(store)} · {store.planTier}
                        </span>
                      </span>
                      {selected && <Check className="h-4 w-4 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <nav className="flex-1 space-y-5 px-3 py-2" aria-label="Console navigation">
          <div className="space-y-1">
            <p className="console-sidebar-label px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.08em]">
              Store tools
            </p>
            {PRODUCT_NAV.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                title={label}
                className={({ isActive }) => cn(
                  'console-sidebar-link flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                  isActive || (to === '/appearance' && location.pathname === '/workspace')
                    ? 'console-sidebar-link-active'
                    : 'console-sidebar-link-idle'
                )}
              >
                <Icon size={15} />
                <span className="console-sidebar-text">{label}</span>
              </NavLink>
            ))}
          </div>

          {isInternal && (
            <div className="space-y-1">
              <p className="console-sidebar-label px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.08em]">
                Admin
              </p>
              {adminNavItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  className={({ isActive }) => cn(
                    'console-sidebar-link flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                    isActive
                      ? 'console-sidebar-link-active'
                      : 'console-sidebar-link-idle'
                  )}
                >
                  <Icon size={15} />
                  <span className="console-sidebar-text">{label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="console-sidebar-footer flex flex-col p-3">
          <NavLink
            to="/stores"
            className={({ isActive }) => cn(
              'console-sidebar-link flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
              isActive || location.pathname === '/'
                ? 'console-sidebar-link-active'
                : 'console-sidebar-link-idle'
            )}
            title="Stores"
          >
            <LayoutGrid size={15} />
            <span className="console-sidebar-text">Stores</span>
          </NavLink>

          <div className="my-2 h-px w-full bg-white/5" />

          <button
            onClick={handleLogout}
            className="console-sidebar-link console-sidebar-link-idle group flex w-full items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-red-500/10"
            title={`Sign out of ${user?.email}`}
          >
            <LogOut size={15} className="text-slate-400 transition-colors group-hover:text-red-400" />
            <div className="console-sidebar-text flex min-w-0 flex-col text-left">
              <span className="text-[13px] font-semibold leading-tight text-slate-200 transition-colors group-hover:text-red-400">Sign out</span>
              <span className="truncate text-[11px] font-medium text-slate-400 transition-colors group-hover:text-red-300/70">{user?.email}</span>
            </div>
          </button>
        </div>
      </aside>

      <main className="console-main relative flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
